import React, { useRef } from "react";
import { Play, Pause, ChevronDown, X, GripVertical } from "lucide-react";
import { useApp } from "../context/AppContext";

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function PlayerBar() {
  const {
    current, playing, togglePlay, progress, duration, seek,
    playerMinimized, setPlayerMinimized, playerClosed, setPlayerClosed,
    playerPos, setPlayerPos,
  } = useApp();

  const dragRef = useRef({ active: false, moved: false, dx: 0, dy: 0 });
  const elRef = useRef(null);

  if (!current || playerClosed) return null;

  const pct = duration ? (progress / duration) * 100 : 0;
  const positioned = !!playerPos;

  // Drag logic shared by both views. `onClick` runs only if it was a tap (no drag).
  const startDrag = (e) => {
    const el = elRef.current;
    if (!el) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      w: rect.width,
      h: rect.height,
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", endDrag);
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 4) d.moved = true;
    if (!d.moved) return;
    let x = e.clientX - d.dx;
    let y = e.clientY - d.dy;
    x = Math.max(8, Math.min(x, window.innerWidth - d.w - 8));
    y = Math.max(8, Math.min(y, window.innerHeight - d.h - 8));
    setPlayerPos({ x, y });
  };
  const endDrag = () => {
    dragRef.current.active = false;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", endDrag);
  };

  const posStyle = positioned ? { left: playerPos.x, top: playerPos.y } : {};

  if (playerMinimized) {
    return (
      <div
        ref={elRef}
        data-testid="player-mini-wrapper"
        className={`fixed z-40 ${positioned ? "" : "top-[64px] right-3"}`}
        style={posStyle}
      >
        <div className="flex items-center gap-1 rounded-full bg-[#141416]/95 backdrop-blur-xl border border-white/10 shadow-2xl pl-1.5 pr-1.5 py-1.5">
          <span
            onPointerDown={startDrag}
            data-testid="player-mini-drag"
            className="flex items-center justify-center text-white/35 hover:text-white/70 cursor-grab active:cursor-grabbing touch-none px-0.5"
            title="Drag to move"
          >
            <GripVertical size={14} />
          </span>
          <button
            data-testid="player-restore-pill"
            onClick={() => { if (!dragRef.current.moved) setPlayerMinimized(false); }}
            className="flex items-center gap-2 hover:opacity-90"
          >
            <span className="relative w-8 h-8 rounded-full overflow-hidden shrink-0">
              <img src={current.cover} alt={current.title} className="w-full h-full object-cover" />
              {playing && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <span className="flex items-end gap-0.5 h-2.5">
                    <span className="eq-bar" style={{ animationDelay: "0s" }} />
                    <span className="eq-bar" style={{ animationDelay: "0.2s" }} />
                    <span className="eq-bar" style={{ animationDelay: "0.4s" }} />
                  </span>
                </span>
              )}
            </span>
            <span className="text-[12px] font-medium text-white max-w-[90px] truncate">{current.title}</span>
          </button>
          <button
            data-testid="player-mini-toggle"
            onClick={togglePlay}
            className="w-7 h-7 rounded-full bg-white text-black flex items-center justify-center hover:bg-white/85 transition-colors shrink-0"
          >
            {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
          </button>
          <button
            data-testid="player-mini-close"
            onClick={() => setPlayerClosed(true)}
            aria-label="Close player"
            className="w-7 h-7 rounded-full text-white/50 flex items-center justify-center hover:bg-white/10 hover:text-white transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={elRef}
      data-testid="player-wrapper"
      className={`fixed z-40 w-full max-w-[480px] ${positioned ? "" : "bottom-0 left-1/2 -translate-x-1/2"}`}
      style={posStyle}
    >
      <div className="mx-2 mb-2 rounded-2xl bg-[#141416]/95 backdrop-blur-xl border border-white/10 shadow-2xl">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <span
            onPointerDown={startDrag}
            data-testid="player-drag"
            className="flex items-center justify-center text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing touch-none -ml-1"
            title="Drag to move"
          >
            <GripVertical size={16} />
          </span>
          <div className="relative w-11 h-11 rounded-lg overflow-hidden shrink-0">
            <img src={current.cover} alt={current.title} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-white truncate">{current.title}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              {playing && (
                <span className="flex items-end gap-0.5 h-3.5">
                  <span className="eq-bar" style={{ animationDelay: "0s" }} />
                  <span className="eq-bar" style={{ animationDelay: "0.2s" }} />
                  <span className="eq-bar" style={{ animationDelay: "0.4s" }} />
                </span>
              )}
              <span className="text-[11px] text-white/40">
                {fmt(progress)} / {fmt(duration)}
              </span>
            </div>
          </div>
          <button
            data-testid="player-minimize-btn"
            onClick={() => setPlayerMinimized(true)}
            aria-label="Minimize player"
            className="w-9 h-9 rounded-full bg-white/10 text-white/70 flex items-center justify-center hover:bg-white/20 hover:text-white transition-colors shrink-0"
          >
            <ChevronDown size={18} />
          </button>
          <button
            data-testid="player-toggle-btn"
            onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-white/85 transition-colors shrink-0"
          >
            {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
          </button>
          <button
            data-testid="player-close-btn"
            onClick={() => setPlayerClosed(true)}
            aria-label="Close player"
            className="w-8 h-8 rounded-full text-white/45 flex items-center justify-center hover:bg-white/10 hover:text-white transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-3 pb-2.5">
          <input
            type="range"
            min={0}
            max={duration || 0}
            value={progress}
            onChange={(e) => seek(Number(e.target.value))}
            className="w-full h-1 appearance-none rounded-full cursor-pointer"
            style={{
              background: `linear-gradient(90deg, #8b5cf6 ${pct}%, rgba(255,255,255,0.12) ${pct}%)`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
